import { imageOrigin } from '@maxpreps/network/src/origins';
import getNextConfig from '../../constants/getNextConfig';
import { getNextPowerOf } from '../primitiveHelpers/numberHelpers';

const { appEnv } = getNextConfig().publicRuntimeConfig;

export const getNextPowerForImageSize = (n: number): number =>
  Math.min(getNextPowerOf(n, 2), 1024);

// Modify a standard pro photogrpahy url. 'size type' and 'size' live in shared '/' split
const isValidUrl = (url: string): boolean => (url?.length ?? 0) > 0;

export const isProPhotoUrl = (url: string): boolean =>
  isValidUrl(url) && url.indexOf('/Gallery/') > -1;

// new landscape dimensions (1 full size photo) - 768x567
// old landscape dimesinos (1 full size photo) - 615x454

export const PRO_PHOTOGRAPHY_LANDSCAPE_RATIO = 1.3552;
export const PRO_PHOTOGRAPHY_PORTRAIT_RATIO = 0.7384;

export const resizeProPhoto = (
  url: string,
  sizeType: number,
  width: number,
  height?: number
): string => {
  if (!isProPhotoUrl(url)) {
    return url;
  }

  // make sure we have an origin to work with
  let formattedUrl = url;
  if (formattedUrl.indexOf('http') === -1) {
    formattedUrl = imageOrigin + formattedUrl;
  }

  const parts = formattedUrl.split('/');
  parts[6] = `${sizeType || 4},${width || ''},${height || ''}`;
  return parts.join('/');
};

// modifying size type has not been implemented yet!
export const resizeMascotImage = (
  url: string,
  sizeType: number | null,
  width: number,
  height: number
): string | undefined => {
  let actualWidth = width;
  let actualHeight = height;

  // if this is a square image request,
  // make it a power of 2 for increased chance of cache use
  if (actualWidth && actualWidth === actualHeight) {
    const powerOf2 = getNextPowerForImageSize(width);

    actualWidth = powerOf2;
    actualHeight = powerOf2;
  }

  return resizeFastlyImage(url, {
    width: actualWidth,
    height: actualHeight
  });
};

// TODO make enum
export const FASTLY_FIT: { [key: string]: string } = {
  Bounds: 'bounds',
  Cover: 'cover',
  Crop: 'crop'
};

// Adapted from https://developer.fastly.com/reference/io/#query-parameters - could be improved with more specific types/enums
export interface IFastlyImageOptions {
  remove?: string[]; // Custom option - removes provided defaults
  auto?: any; // Enable optimization features automatically.
  'bg-color'?: string; // Set the background color of an image.
  blur?: any; // Set the blurriness of the output image.
  brightness?: any; // Set the brightness of the output image.
  canvas?: any; // Increase the size of the canvas around an image.
  contrast?: any; // Set the contrast of the output image.
  crop?: string; // Remove pixels from an image.
  disable?: any; // Disable functionality that is enabled by default.
  dpr?: any; // Serve correctly sized images for devices that expose a device pixel ratio.
  enable?; // Enable functionality that is disabled by default.
  fit?: string; // Set how the image will fit within the size bounds provided.
  format?: any; // Specify the output format to convert the image to.
  frame?: any; // Extract the first frame from an animated image sequence.
  height?: any; // Resize the height of the image.
  level?: any; // Specify the level constraints when converting to video.
  optimize?: any; // Automatically apply optimal quality compression.
  orient?: any; // Change the cardinal orientation of the image.
  pad?: any; // Add pixels to the edge of an image.
  precrop?: any; // Remove pixels from an image before any other transformations occur.
  profile?: any; // Specify the profile class of application when converting to video.
  quality?: any; // Optimize the image to the given compression level for lossy file formatted images.
  'resize-filter'?: any; // Specify the resize filter used when resizing images.
  saturation?: any; // Set the saturation of the output image.
  sharpen?: any; // Set the sharpness of the output image.
  trim?: any; // Remove pixels from the edge of an image.
  width: number; // Resize the width of the image.
}

// This should only temporary be needed as we migrate off cloudfront.
// The react ticket (https://jira.cbsi.com/browse/MP-13045)
// is going ahead of the api ticket (https://jira.cbsi.com/browse/MP-13047)
// once the API ticket is completed (~8/1/2022) we should be able to kill this object
// and it's references
const devCloudFrontOrigin = 'https://d1yf833igi2o06.cloudfront.net';
const productionCloudFrontOrigin = 'https://dw3jhbqsbya58.cloudfront.net';
const cloudFrontImageOrigin = {
  dev: devCloudFrontOrigin,
  development: devCloudFrontOrigin,
  staging: productionCloudFrontOrigin,
  production: productionCloudFrontOrigin
};

// https://docs.fastly.com/en/guides/image-optimization-api
export const resizeFastlyImage = (
  url: string,
  options: IFastlyImageOptions,
  addDefaults: boolean = true
) => {
  if (!isValidUrl(url) || !options) {
    return url;
  }

  const modifiedUrl = new URL(
    // Ryan: We used to only swap out in prod. On 6/21/2021 we started doing dev too and using the dev s3 image origin for the replacement
    url.replace(cloudFrontImageOrigin[appEnv], ''), // Ryan: Not thrilled about this. Are there other s3 origins that also map to imageOrigin? I wolnd't want 5 replaces here...
    imageOrigin // still needed in scenario where an origin is specified
  );

  const query: URLSearchParams = modifiedUrl.searchParams;

  // scrub 'provided' options from the source image
  (options.remove ?? []).forEach(item => query.delete(item));

  // adding some defaults
  if (addDefaults) {
    // some how these properties are getting mapped onto anythuing else using
    // a for of loop. No idea how this is happening. Insepect on
    // getUserDataFromMemberCookie() return for example
    if (!query.get('auto')) query.set('auto', 'webp');
    if (!query.get('format')) query.set('format', 'pjpg');
  }

  // i'd like to add some logic to make sure width and height
  // are an exponent of 2. It's really the job of css to manage the ui.
  // i think it should be the job of this method to ensure the maximum
  // cachability of the component, which exp of 2 does.
  // dummy code - if(options.width) options.width = options.width % 2
  // dummy code - if(options.height) options.height = options.height % 2

  // loop through all options and apply them to the query string
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of Object.entries(options)) {
    query.set(key, value);
  }

  return modifiedUrl.toString();
};
